import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { collection, getDocs, doc, getDoc, limit, query } from "firebase/firestore";
import { db } from "../lib/firebase";
import { useAuth } from "../contexts/AuthContext";
import { useRadioStore } from "../stores/radioStore";
import { Radio as RadioIcon, Star, Music, Play, Loader2, ChevronRight, Zap } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function WeekCalendar({ schedules }) {
  const today = new Date().getDay();
  return (
    <div className="grid grid-cols-7 gap-1 mt-4">
      {DAYS.map((day, i) => {
        const daySchedules = schedules.filter((s) => s.dayOfWeek === i);
        const isToday = i === today;
        return (
          <div key={day} className={`rounded-xl p-2 min-h-[80px] border transition-colors ${isToday ? "border-[#B026FF]/50 bg-[#B026FF]/5" : "border-white/5 bg-[#111]"}`}>
            <p className={`text-xs font-bold mb-2 ${isToday ? "text-[#B026FF]" : "text-zinc-500"}`}>{day}</p>
            <div className="space-y-1">
              {daySchedules.map((s, j) => (
                <div key={j} className="text-[9px] bg-[#B026FF]/20 text-[#B026FF] px-1 py-0.5 rounded truncate">
                  {s.startTime}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function StationCard({ station }) {
  const { user } = useAuth();
  const { playStation, playing, currentStation, stop } = useRadioStore();
  const [tuning, setTuning] = useState(false);
  const isPlaying = playing && currentStation?.id === station.id;

  const handlePlay = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isPlaying) { stop(); return; }
    setTuning(true);
    try {
      const headers = {};
      if (user) {
        const token = await user.getIdToken(true);
        headers["Authorization"] = `Bearer ${token}`;
      }
      const res = await fetch(`${API_URL}/api/radio/playback-plan/${station.id}`, { headers });
      const plan = res.ok ? await res.json() : null;
      await playStation({ ...station }, plan);
    } catch (e) {
      await playStation({ ...station }, null);
    }
    setTuning(false);
  };

  return (
    <div data-testid={`station-card-${station.id}`} className="group bg-[#111] border border-white/5 rounded-2xl overflow-hidden hover:bg-[#1a1a1a] hover:border-[#B026FF]/30 hover:shadow-[0_0_20px_rgba(176,38,255,0.1)] transition-all duration-300 flex items-start gap-4 p-4">
      <Link to={`/radio/${station.id}`} className="w-16 h-16 rounded-xl bg-[#1a1a1a] overflow-hidden flex-shrink-0">
        {station.logoURL ? (
          <img src={station.logoURL} alt={station.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <RadioIcon size={20} className="text-zinc-600" />
          </div>
        )}
      </Link>
      <Link to={`/radio/${station.id}`} className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <h3 className="text-white font-semibold text-sm truncate">{station.name}</h3>
          {station.isLive && (
            <span className="flex items-center gap-1 text-[9px] bg-red-500 text-white font-bold px-1.5 py-0.5 rounded-full animate-pulse flex-shrink-0">
              <Zap size={8} /> LIVE
            </span>
          )}
          {station.autodjEnabled && (
            <span className="text-[10px] bg-[#B026FF]/20 text-[#B026FF] px-1.5 py-0.5 rounded-full flex-shrink-0">Auto-DJ</span>
          )}
        </div>
        <p className="text-zinc-500 text-xs line-clamp-2 mb-2">{station.description}</p>
        <div className="flex items-center gap-3 text-zinc-600 text-xs">
          {station.genre && <span className="bg-white/5 px-2 py-0.5 rounded-full">{station.genre}</span>}
          <span className="flex items-center gap-1"><Music size={10} /> {station.trackCount || 0} tracks</span>
        </div>
      </Link>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          data-testid={`play-station-${station.id}`}
          onClick={handlePlay}
          disabled={tuning}
          className={`w-9 h-9 rounded-full flex items-center justify-center transition-all hover:scale-105 disabled:opacity-60 ${
            isPlaying ? "bg-white/10 border border-white/20" : ""
          }`}
          style={!isPlaying ? { background: "linear-gradient(135deg, #B026FF, #7C3AED)" } : {}}
        >
          {tuning ? (
            <Loader2 size={14} className="text-white animate-spin" />
          ) : isPlaying ? (
            <span className="w-2.5 h-2.5 rounded-full bg-[#00ff77] animate-pulse" />
          ) : (
            <Play size={14} className="text-white ml-0.5" fill="white" />
          )}
        </button>
        <Link to={`/radio/${station.id}`}>
          <ChevronRight size={16} className="text-zinc-600 group-hover:text-[#B026FF] transition-colors" />
        </Link>
      </div>
    </div>
  );
}

export default function Radio() {
  const [stations, setStations] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { playStation, playing, currentStation, stop } = useRadioStore();
  const [featuredTuning, setFeaturedTuning] = useState(false);
  const isFeaturedPlaying = playing && featured && currentStation?.id === featured.id;

  const handleFeaturedPlay = async (e) => {
    e.preventDefault();
    if (!featured) return;
    if (isFeaturedPlaying) { stop(); return; }
    setFeaturedTuning(true);
    try {
      const headers = {};
      if (user) {
        const token = await user.getIdToken(true);
        headers["Authorization"] = `Bearer ${token}`;
      }
      const res = await fetch(`${API_URL}/api/radio/playback-plan/${featured.id}`, { headers });
      const plan = res.ok ? await res.json() : null;
      await playStation({ ...featured }, plan);
    } catch (e) {
      await playStation({ ...featured }, null);
    }
    setFeaturedTuning(false);
  };

  useEffect(() => { loadRadio(); }, []);

  const loadRadio = async () => {
    try {
      // Fetch all stations without orderBy — old migrated docs may lack createdAt
      // (Firestore orderBy silently excludes docs that don't have the field)
      const [stationsSnap, settingsDoc] = await Promise.all([
        getDocs(collection(db, "stations")),
        getDoc(doc(db, "siteSettings", "global")),
      ]);

      const stationsData = stationsSnap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((d) => !d.status || d.status === "approved")
        .sort((a, b) => (b.createdAt?.seconds || b.createdAt || 0) - (a.createdAt?.seconds || a.createdAt || 0));
      setStations(stationsData);

      // Pick station of the month from siteSettings, or fall back to first station
      const siteSettings = settingsDoc.exists() ? settingsDoc.data() : {};
      if (siteSettings.stationOfMonth) {
        const spotm = stationsData.find((s) => s.id === siteSettings.stationOfMonth) || stationsData[0];
        setFeatured(spotm || null);
      } else if (stationsData.length > 0) {
        setFeatured(stationsData[0]);
      }

      // Load this week's schedules — no orderBy to avoid excluding legacy docs
      const schedulesSnap = await getDocs(query(collection(db, "schedules"), limit(200)));
      const schedulesData = schedulesSnap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.dayOfWeek || 0) - (b.dayOfWeek || 0));
      setSchedules(schedulesData);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  return (
    <div data-testid="radio-page" className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="text-center py-8">
        <h1 className="text-4xl sm:text-5xl font-black mb-3">
          <span className="text-white">STAGEPASS </span>
          <span className="text-gradient-purple">RADIO</span>
        </h1>
        <p className="text-zinc-400 text-sm max-w-md mx-auto mb-6">
          Global Creator Broadcast Network. Tune in live or let Auto-DJ keep the music flowing.
        </p>
        <Link
          to="/dashboard/station"
          data-testid="start-radio-btn"
          className="inline-flex items-center gap-2 bg-[#B026FF] text-white font-bold text-sm px-6 py-2.5 rounded-full hover:bg-[#9b1ee6] transition-colors"
        >
          <RadioIcon size={14} />
          Start Your Radio Show Today
        </Link>
      </div>

      {/* Station of the Month */}
      {featured && (
        <section data-testid="station-of-month">
          <div className="flex items-center gap-2 mb-4">
            <Star size={14} className="text-yellow-400 fill-yellow-400" />
            <span className="text-xs font-bold uppercase tracking-widest text-zinc-400">Station of the Month</span>
          </div>
          <div
            className="block bg-[#111] border border-white/5 rounded-2xl p-6 hover:border-[#B026FF]/30 hover:shadow-[0_0_20px_rgba(176,38,255,0.1)] transition-all duration-300"
          >
            <div className="flex items-start gap-5">
              <div className="w-20 h-20 rounded-xl bg-[#1a1a1a] overflow-hidden flex-shrink-0">
                {featured.logoURL ? (
                  <img src={featured.logoURL} alt={featured.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <RadioIcon size={24} className="text-zinc-600" />
                  </div>
                )}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  {featured.genre && <span className="text-[10px] bg-[#B026FF]/20 text-[#B026FF] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">{featured.genre}</span>}
                </div>
                <h2 className="text-white text-xl font-bold mb-1">{featured.name}</h2>
                <p className="text-zinc-400 text-sm mb-3">{featured.description}</p>
                <div className="flex items-center gap-4 text-zinc-500 text-xs flex-wrap">
                  <span>{featured.showCount || 0} shows</span>
                  {featured.autodjEnabled && (
                    <span className="flex items-center gap-1 text-[#B026FF]">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#B026FF] animate-pulse" />
                      Auto-DJ Active
                    </span>
                  )}
                  <button
                    data-testid="featured-play-btn"
                    onClick={handleFeaturedPlay}
                    disabled={featuredTuning}
                    className="flex items-center gap-2 font-bold text-xs px-4 py-1.5 rounded-full transition-all hover:scale-105 disabled:opacity-60 text-white"
                    style={{ background: "linear-gradient(135deg, #B026FF, #7C3AED)" }}
                  >
                    {featuredTuning ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : isFeaturedPlaying ? (
                      <><span className="w-2 h-2 rounded-full bg-[#00ff77] animate-pulse" /> Live</>
                    ) : (
                      <><Play size={12} fill="white" /> Tune In</>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* 7-day schedule preview */}
            <div className="mt-4 border-t border-white/5 pt-4">
              <p className="text-xs font-semibold text-zinc-500 mb-2 uppercase tracking-wider">This Week's Schedule</p>
              <WeekCalendar schedules={schedules.filter((s) => s.stationId === featured.id)} />
            </div>
          </div>
        </section>
      )}

      {/* All Stations */}
      {stations.length > 1 && (
        <section>
          <h2 className="text-base font-bold text-white mb-4">All Stations</h2>
          {loading ? (
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="bg-[#111] rounded-2xl h-24 animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {stations.slice(1).map((s) => <StationCard key={s.id} station={s} />)}
            </div>
          )}
        </section>
      )}

      {stations.length === 0 && !loading && (
        <div className="text-center py-16 text-zinc-500">
          <RadioIcon size={32} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm">No stations yet. Be the first to broadcast.</p>
        </div>
      )}
    </div>
  );
}
